/**
 * Global axios response cache for reference endpoints.
 *
 * KEEPS: safe short-TTL response cache for a whitelist of *reference*
 *        endpoints (branches, users/mini, installment plans, settings)
 *        that rarely change but get re-fetched dozens of times per
 *        session when the user tabs around.
 *
 * DOES NOT: monkey-patch axios internals or dedupe in-flight requests
 *           — those were removed after an issue was reported where
 *           dashboard `Promise.all` calls stalled behind the shim.
 *
 * SAFETY:
 *   • Only successful (2xx) GET responses to whitelisted URLs are cached.
 *   • Any non-GET (POST / PUT / PATCH / DELETE) automatically invalidates
 *     entries whose URL prefix matches the mutated resource.
 *   • Cache entries expire; expired entries fall through to a real fetch.
 */
import axios from 'axios';

const CACHE = new Map();     // url -> { at, response }

// URL substring -> cache TTL in ms. Only reference endpoints go here.
const CACHEABLE = [
  { match: '/api/branches',           ttl: 300_000 },
  { match: '/api/users/mini',         ttl: 180_000 },
  { match: '/api/users/roles',        ttl: 900_000 },
  { match: '/api/users/permissions/', ttl: 900_000 },
  { match: '/api/installment-plans',  ttl: 300_000 },
  { match: '/api/company-settings',   ttl: 600_000 },
  { match: '/api/settings/company',   ttl: 600_000 },
];

function cacheEntryFor(url) {
  for (const c of CACHEABLE) if (url.includes(c.match)) return c;
  return null;
}

function normalize(config) {
  const base = axios.defaults.baseURL || '';
  const url  = (config.url || '').replace(base, '');
  const params = config.params ? '?' + new URLSearchParams(config.params).toString() : '';
  return url + params;
}

// -------------------------------------- Request interceptor
axios.interceptors.request.use((config) => {
  if ((config.method || 'get').toLowerCase() !== 'get') return config;
  const key = normalize(config);
  const entry = cacheEntryFor(key);
  if (!entry) return config;

  const now = Date.now();
  const hit = CACHE.get(key);
  if (hit && (now - hit.at) < entry.ttl) {
    // Short-circuit with a resolving adapter that returns the cached response.
    config.adapter = () => Promise.resolve({
      ...hit.response,
      config,
      __fromCache: true,
    });
    return config;
  }
  // Mark this config so the response interceptor knows to cache the result.
  config.__cacheKey = key;
  return config;
});

// -------------------------------------- Response interceptor
axios.interceptors.response.use((response) => {
  const cfg = response.config || {};
  const method = (cfg.method || 'get').toLowerCase();

  // Cache successful GETs of whitelisted URLs
  if (method === 'get' && cfg.__cacheKey && !response.__fromCache &&
      response.status >= 200 && response.status < 300) {
    CACHE.set(cfg.__cacheKey, { at: Date.now(), response });
  }

  // Any write invalidates cached refs whose URL prefix matches the target.
  if (method !== 'get' && cfg.url) {
    const target = normalize(cfg).split('?')[0];
    for (const k of Array.from(CACHE.keys())) {
      if (k.split('?')[0].startsWith(target)) CACHE.delete(k);
    }
  }
  return response;
});

export function bustAxiosCache(prefix = '') {
  if (!prefix) { CACHE.clear(); return; }
  for (const k of Array.from(CACHE.keys())) if (k.includes(prefix)) CACHE.delete(k);
}
